/**
 * Profiler data model (pure, no React): the frames the engine records
 * (`PrismView.profilerFrames`, see engine/prism.ts), a bounded ring buffer
 * of them, the frame-history graph series, the Top Systems table over a
 * frame range, percentiles, and capture export / import (JSON) and CSV.
 */

/** One timed engine scope (`prof!`) of a frame. */
export interface CpuScope {
  name: string;
  /** Milliseconds after the frame start. */
  start: number;
  ms: number;
  /** Nesting depth (0 = top level). */
  depth: number;
}

/** One GPU pass (timestamp queries). */
export interface GpuPass {
  name: string;
  /** Milliseconds after the first timed pass. */
  start: number;
  ms: number;
}

export interface ProfilerFrame {
  seq: number;
  /** Clock time of the frame start (ms, arbitrary origin). */
  start: number;
  /** CPU time of the frame (update + render encode). */
  ms: number;
  /** Time since the previous frame started (0 when unknown). */
  interval: number;
  cpu: CpuScope[];
  gpu: GpuPass[] | null;
  gpuMs: number | null;
  counters: Record<string, number>;
}

/* ------------------------------------------------------------------ */
/* Ring buffer                                                         */

export const DEFAULT_RING_CAPACITY = 600;

/** Bounded, seq-ordered frame store: pushing past the capacity drops the oldest frames. */
export class FrameRing {
  private items: ProfilerFrame[] = [];

  constructor(private cap = DEFAULT_RING_CAPACITY) {}

  get capacity(): number {
    return this.cap;
  }

  set capacity(n: number) {
    this.cap = Math.max(1, Math.floor(n));
    this.trim();
  }

  get size(): number {
    return this.items.length;
  }

  /** Last sequence number held (-1 when empty). */
  get lastSeq(): number {
    return this.items.length ? this.items[this.items.length - 1]!.seq : -1;
  }

  /** Appends frames; older or duplicate sequence numbers are ignored. */
  push(frames: readonly ProfilerFrame[]): number {
    let added = 0;
    for (const f of frames) {
      if (f.seq <= this.lastSeq) continue;
      this.items.push(f);
      added++;
    }
    this.trim();
    return added;
  }

  clear(): void {
    this.items = [];
  }

  /** Frames oldest first (the live array: do not mutate). */
  frames(): readonly ProfilerFrame[] {
    return this.items;
  }

  get(seq: number): ProfilerFrame | undefined {
    const i = this.indexOf(seq);
    return i >= 0 ? this.items[i] : undefined;
  }

  /** Index of `seq` (binary search; -1 when not held). */
  indexOf(seq: number): number {
    let lo = 0;
    let hi = this.items.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const s = this.items[mid]!.seq;
      if (s === seq) return mid;
      if (s < seq) lo = mid + 1;
      else hi = mid - 1;
    }
    return -1;
  }

  /** Frames with `from <= seq <= to` (either order). */
  range(from: number, to: number): ProfilerFrame[] {
    const [a, b] = from <= to ? [from, to] : [to, from];
    return this.items.filter((f) => f.seq >= a && f.seq <= b);
  }

  private trim(): void {
    if (this.items.length > this.cap) this.items.splice(0, this.items.length - this.cap);
  }
}

/* ------------------------------------------------------------------ */
/* Categories                                                          */

export interface Category {
  id: string;
  label: string;
  color: string;
}

/** Stacked CPU categories of the frame-history graph (Unity's CPU Usage module). */
export const CATEGORIES: readonly Category[] = [
  { id: 'rendering', label: 'Rendering', color: '#5b8cff' },
  { id: 'scripts', label: 'Scripts', color: '#34d399' },
  { id: 'physics', label: 'Physics', color: '#fbbf24' },
  { id: 'animation', label: 'Animation', color: '#a78bfa' },
  { id: 'effects', label: 'Particles & Effects', color: '#f472b6' },
  { id: 'ui', label: 'UI', color: '#22d3ee' },
  { id: 'audio', label: 'Audio', color: '#fb923c' },
  { id: 'streaming', label: 'Streaming & Jobs', color: '#94a3b8' },
  { id: 'network', label: 'Network', color: '#f87171' },
  { id: 'other', label: 'Other', color: '#64748b' },
];

const PREFIX_CATEGORY: Record<string, string> = {
  render: 'rendering',
  transforms: 'rendering',
  scripts: 'scripts',
  physics: 'physics',
  liquids: 'physics',
  animation: 'animation',
  expressions: 'animation',
  dynamics: 'animation',
  boneAttach: 'animation',
  particles: 'effects',
  sprites: 'effects',
  decals: 'effects',
  ui: 'ui',
  audio: 'audio',
  terrain: 'streaming',
  scene: 'streaming',
  jobs: 'streaming',
  net: 'network',
};

/** Category id of a scope name (`physics.step` → `physics`). */
export function categoryOf(name: string): string {
  const prefix = name.split('.')[0] ?? name;
  // `liquids.surface` animates a mesh; the physics step is `liquids.physics`.
  if (name === 'liquids.surface') return 'effects';
  return PREFIX_CATEGORY[prefix] ?? 'other';
}

/** Per-category milliseconds of one frame (top-level scopes; untracked time goes to Other). */
export function frameCategories(frame: ProfilerFrame): number[] {
  const out = CATEGORIES.map(() => 0);
  const other = CATEGORIES.findIndex((c) => c.id === 'other');
  let tracked = 0;
  for (const s of frame.cpu) {
    if (s.depth !== 0) continue;
    const i = CATEGORIES.findIndex((c) => c.id === categoryOf(s.name));
    out[i >= 0 ? i : other]! += s.ms;
    tracked += s.ms;
  }
  out[other]! += Math.max(0, frame.ms - tracked);
  return out;
}

/** Series of the frame-history graph. */
export interface HistoryPoint {
  seq: number;
  /** Frame CPU ms (sum of `stack`). */
  ms: number;
  stack: number[];
  gpuMs: number | null;
  interval: number;
}

export function frameHistory(frames: readonly ProfilerFrame[]): HistoryPoint[] {
  return frames.map((f) => {
    const stack = frameCategories(f);
    return { seq: f.seq, ms: stack.reduce((a, b) => a + b, 0), stack, gpuMs: f.gpuMs, interval: f.interval };
  });
}

/* ------------------------------------------------------------------ */
/* Self time and the Top Systems table                                 */

/** Self milliseconds of each scope (its time minus its direct children). Scopes must be in start order. */
export function selfTimes(scopes: readonly CpuScope[]): number[] {
  const self = scopes.map((s) => s.ms);
  const stack: number[] = [];
  scopes.forEach((s, i) => {
    while (stack.length) {
      const top = scopes[stack[stack.length - 1]!]!;
      if (top.depth < s.depth && s.start < top.start + top.ms + 1e-9) break;
      stack.pop();
    }
    const parent = stack[stack.length - 1];
    if (parent !== undefined && scopes[parent]!.depth === s.depth - 1) self[parent] = Math.max(0, self[parent]! - s.ms);
    stack.push(i);
  });
  return self;
}

export interface SystemRow {
  name: string;
  category: string;
  /** Total calls in the range. */
  calls: number;
  callsPerFrame: number;
  /** Average per frame of the range (frames without the scope count as 0). */
  avgMs: number;
  /** Worst frame (summed over the scope's calls in that frame). */
  maxMs: number;
  selfAvgMs: number;
  selfMaxMs: number;
  totalMs: number;
  /** Share of the average frame time (0..1). */
  share: number;
}

export type SystemSortKey = 'name' | 'avgMs' | 'maxMs' | 'selfAvgMs' | 'calls' | 'share';

/** Top Systems over `frames` (one frame = that frame's own numbers), slowest first. */
export function topSystems(frames: readonly ProfilerFrame[]): SystemRow[] {
  const n = frames.length;
  if (!n) return [];
  const acc = new Map<string, { calls: number; total: number; self: number; max: number; selfMax: number }>();
  let frameTotal = 0;
  for (const f of frames) {
    frameTotal += f.ms;
    const self = selfTimes(f.cpu);
    const perFrame = new Map<string, { ms: number; self: number }>();
    f.cpu.forEach((s, i) => {
      const a = acc.get(s.name) ?? { calls: 0, total: 0, self: 0, max: 0, selfMax: 0 };
      a.calls++;
      a.total += s.ms;
      a.self += self[i]!;
      acc.set(s.name, a);
      const p = perFrame.get(s.name) ?? { ms: 0, self: 0 };
      p.ms += s.ms;
      p.self += self[i]!;
      perFrame.set(s.name, p);
    });
    for (const [name, p] of perFrame) {
      const a = acc.get(name)!;
      a.max = Math.max(a.max, p.ms);
      a.selfMax = Math.max(a.selfMax, p.self);
    }
  }
  const frameAvg = frameTotal / n;
  const rows: SystemRow[] = [...acc].map(([name, a]) => ({
    name,
    category: categoryOf(name),
    calls: a.calls,
    callsPerFrame: a.calls / n,
    avgMs: a.total / n,
    maxMs: a.max,
    selfAvgMs: a.self / n,
    selfMaxMs: a.selfMax,
    totalMs: a.total,
    share: frameAvg > 0 ? a.total / n / frameAvg : 0,
  }));
  return sortSystems(rows, 'avgMs', true);
}

export function sortSystems(rows: SystemRow[], key: SystemSortKey, descending: boolean): SystemRow[] {
  const dir = descending ? -1 : 1;
  return [...rows].sort((a, b) => {
    const r = key === 'name' ? a.name.localeCompare(b.name) : (a[key] as number) - (b[key] as number);
    return r === 0 ? a.name.localeCompare(b.name) : r * dir;
  });
}

/** GPU passes summed by name over `frames`: average / max per frame, slowest first. */
export function gpuPasses(frames: readonly ProfilerFrame[]): { name: string; avgMs: number; maxMs: number; count: number }[] {
  const timed = frames.filter((f) => f.gpu);
  if (!timed.length) return [];
  const acc = new Map<string, { total: number; max: number; count: number }>();
  for (const f of timed) {
    const per = new Map<string, { ms: number; n: number }>();
    for (const p of f.gpu!) {
      const e = per.get(p.name) ?? { ms: 0, n: 0 };
      e.ms += p.ms;
      e.n++;
      per.set(p.name, e);
    }
    for (const [name, e] of per) {
      const a = acc.get(name) ?? { total: 0, max: 0, count: 0 };
      a.total += e.ms;
      a.max = Math.max(a.max, e.ms);
      a.count += e.n;
      acc.set(name, a);
    }
  }
  return [...acc]
    .map(([name, a]) => ({ name, avgMs: a.total / timed.length, maxMs: a.max, count: a.count / timed.length }))
    .sort((a, b) => b.avgMs - a.avgMs);
}

/* ------------------------------------------------------------------ */
/* Statistics                                                          */

/** Percentile `p` (0..100) of `values` with linear interpolation (NaN when empty). */
export function percentile(values: readonly number[], p: number): number {
  if (!values.length) return NaN;
  const sorted = [...values].sort((a, b) => a - b);
  const rank = (Math.min(100, Math.max(0, p)) / 100) * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (rank - lo);
}

export interface FrameStats {
  count: number;
  avgMs: number;
  minMs: number;
  maxMs: number;
  p50: number;
  p95: number;
  p99: number;
  /** From the frame intervals (0 when unknown). */
  fps: number;
  gpuAvgMs: number | null;
}

export function frameStats(frames: readonly ProfilerFrame[]): FrameStats {
  const ms = frames.map((f) => f.ms);
  const intervals = frames.map((f) => f.interval).filter((v) => v > 0);
  const gpu = frames.map((f) => f.gpuMs).filter((v): v is number => typeof v === 'number');
  const avg = (v: number[]) => (v.length ? v.reduce((a, b) => a + b, 0) / v.length : 0);
  const avgInterval = avg(intervals);
  return {
    count: frames.length,
    avgMs: avg(ms),
    minMs: ms.length ? Math.min(...ms) : 0,
    maxMs: ms.length ? Math.max(...ms) : 0,
    p50: percentile(ms, 50),
    p95: percentile(ms, 95),
    p99: percentile(ms, 99),
    fps: avgInterval > 0 ? 1000 / avgInterval : 0,
    gpuAvgMs: gpu.length ? avg(gpu) : null,
  };
}

/** Average of each counter over `frames` (counters missing in a frame are skipped). */
export function counterAverages(frames: readonly ProfilerFrame[]): Record<string, number> {
  const sum: Record<string, [number, number]> = {};
  for (const f of frames)
    for (const [k, v] of Object.entries(f.counters)) {
      const s = (sum[k] ??= [0, 0]);
      s[0] += v;
      s[1]++;
    }
  return Object.fromEntries(Object.entries(sum).map(([k, [t, n]]) => [k, t / n]));
}

/* ------------------------------------------------------------------ */
/* Engine JSON                                                         */

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** Validates / normalises one frame from engine or capture JSON (null when unusable). */
export function parseFrame(raw: unknown): ProfilerFrame | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.seq !== 'number' || !Number.isFinite(r.seq)) return null;
  const scopes = Array.isArray(r.cpu) ? r.cpu : [];
  const cpu: CpuScope[] = scopes
    .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object' && typeof (s as { name?: unknown }).name === 'string')
    .map((s) => ({ name: s.name as string, start: num(s.start), ms: num(s.ms), depth: Math.max(0, Math.floor(num(s.depth))) }));
  const gpu: GpuPass[] | null = Array.isArray(r.gpu)
    ? r.gpu
        .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object' && typeof (s as { name?: unknown }).name === 'string')
        .map((s) => ({ name: s.name as string, start: num(s.start), ms: num(s.ms) }))
    : null;
  const counters: Record<string, number> = {};
  if (r.counters && typeof r.counters === 'object')
    for (const [k, v] of Object.entries(r.counters as Record<string, unknown>)) if (typeof v === 'number' && Number.isFinite(v)) counters[k] = v;
  return {
    seq: r.seq,
    start: num(r.start),
    ms: num(r.ms),
    interval: num(r.interval),
    cpu,
    gpu,
    gpuMs: typeof r.gpuMs === 'number' && Number.isFinite(r.gpuMs) ? r.gpuMs : null,
    counters,
  };
}

/* ------------------------------------------------------------------ */
/* Capture files                                                       */

export const CAPTURE_FORMAT = 'prism-profiler-capture';
export const CAPTURE_VERSION = 1;

export interface CaptureMeta {
  /** Which view was profiled (`play` / `scene`). */
  target?: string;
  /** ISO date of the export. */
  created?: string;
  project?: string;
  gpuSupported?: boolean;
}

export interface ProfilerCapture {
  meta: CaptureMeta;
  frames: ProfilerFrame[];
}

/** A capture file (JSON) of `frames`. */
export function exportCapture(frames: readonly ProfilerFrame[], meta: CaptureMeta = {}): string {
  return JSON.stringify({
    format: CAPTURE_FORMAT,
    version: CAPTURE_VERSION,
    meta: { created: new Date().toISOString(), ...meta },
    frames,
  });
}

/** Reads a capture file; throws a readable error for anything else. */
export function importCapture(text: string): ProfilerCapture {
  let doc: unknown;
  try {
    doc = JSON.parse(text);
  } catch {
    throw new Error('Not a profiler capture (invalid JSON).');
  }
  const d = doc as { format?: unknown; version?: unknown; meta?: unknown; frames?: unknown };
  if (!d || typeof d !== 'object' || d.format !== CAPTURE_FORMAT) throw new Error('Not a profiler capture (missing format tag).');
  if (typeof d.version !== 'number' || d.version > CAPTURE_VERSION) throw new Error(`Unsupported capture version ${String(d.version)}.`);
  if (!Array.isArray(d.frames)) throw new Error('Profiler capture has no frames.');
  const frames = d.frames.map(parseFrame).filter((f): f is ProfilerFrame => f !== null);
  frames.sort((a, b) => a.seq - b.seq);
  const meta = d.meta && typeof d.meta === 'object' ? (d.meta as CaptureMeta) : {};
  return { meta, frames };
}

function csvCell(value: string | number | null): string {
  if (value === null) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(Math.round(value * 1e4) / 1e4) : '';
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * One row per frame: seq, CPU ms, interval, GPU ms, every counter, then the
 * per-frame milliseconds of every scope (summed over its calls).
 */
export function toCsv(frames: readonly ProfilerFrame[]): string {
  const counters = [...new Set(frames.flatMap((f) => Object.keys(f.counters)))].sort();
  const scopes = [...new Set(frames.flatMap((f) => f.cpu.map((s) => s.name)))].sort();
  const header = ['seq', 'cpuMs', 'intervalMs', 'gpuMs', ...counters, ...scopes.map((s) => `${s} (ms)`)];
  const lines = [header.map(csvCell).join(',')];
  for (const f of frames) {
    const per = new Map<string, number>();
    for (const s of f.cpu) per.set(s.name, (per.get(s.name) ?? 0) + s.ms);
    lines.push(
      [f.seq, f.ms, f.interval, f.gpuMs, ...counters.map((c) => f.counters[c] ?? null), ...scopes.map((s) => per.get(s) ?? 0)]
        .map(csvCell)
        .join(','),
    );
  }
  return lines.join('\n') + '\n';
}

/* ------------------------------------------------------------------ */
/* Formatting                                                          */

export function formatMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '–';
  if (ms >= 100) return `${ms.toFixed(0)} ms`;
  if (ms >= 10) return `${ms.toFixed(1)} ms`;
  return `${ms.toFixed(2)} ms`;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '–';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v >= 100 || u === 0 ? v.toFixed(0) : v.toFixed(1)} ${units[u]}`;
}

export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '–';
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e4) return `${(n / 1e3).toFixed(1)}k`;
  return String(Math.round(n));
}
